import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, Mail, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  title_fr: "", title_nl: "", excerpt_fr: "", excerpt_nl: "",
  content_fr: "", content_nl: "", image_url: "", category: "conseils", published: true,
};

const catLabels = { prix: "Prix", primes: "Primes", conseils: "Conseils" };

export const BlogTab = () => {
  const [posts, setPosts] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [subscribers, setSubscribers] = useState([]);

  const fetchSubscribers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/newsletter/admin/subscribers`, { withCredentials: true });
      setSubscribers(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/posts`, { withCredentials: true });
      setPosts(res.data);
    } catch { setPosts([]); }
  }, []);

  useEffect(() => { fetchPosts(); fetchSubscribers(); }, [fetchPosts, fetchSubscribers]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (post) => {
    setEditingId(post.id);
    setForm({
      title_fr: post.title_fr, title_nl: post.title_nl,
      excerpt_fr: post.excerpt_fr || "", excerpt_nl: post.excerpt_nl || "",
      content_fr: post.content_fr, content_nl: post.content_nl,
      image_url: post.image_url || "", category: post.category || "conseils",
      published: post.published,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title_fr || !form.title_nl || !form.content_fr || !form.content_nl) {
      toast.error("Titre et contenu requis en FR et NL");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/blog/admin/posts/${editingId}`, form, { withCredentials: true });
        toast.success("Article mis à jour");
      } else {
        await axios.post(`${API}/blog/admin/posts`, form, { withCredentials: true });
        toast.success("Article créé");
      }
      setDialogOpen(false);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Supprimer définitivement l'article « ${post.title_fr} » ?`)) return;
    try {
      await axios.delete(`${API}/blog/admin/posts/${post.id}`, { withCredentials: true });
      toast.success("Article supprimé");
      fetchPosts();
    } catch { toast.error("Erreur lors de la suppression"); }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(`${API}/blog/admin/posts/${post.id}`, { published: !post.published }, { withCredentials: true });
      toast.success(post.published ? "Article dépublié" : "Article publié");
      fetchPosts();
    } catch { toast.error("Erreur"); }
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const copyEmails = () => {
    navigator.clipboard.writeText(subscribers.map((s) => s.email).join("; "));
    toast.success("Emails copiés dans le presse-papiers");
  };

  const deleteSubscriber = async (sub) => {
    if (!window.confirm(`Désinscrire ${sub.email} ?`)) return;
    try {
      await axios.delete(`${API}/newsletter/admin/subscribers/${sub.id}`, { withCredentials: true });
      toast.success("Abonné supprimé");
      fetchSubscribers();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="blog-admin-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Les articles sont publiés en français ET en néerlandais sur /blog et /nl/blog.
          Mise en forme : ligne "## Titre" pour un sous-titre, "- " pour une liste, **gras**.
        </p>
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="blog-create-button">
          <Plus className="w-4 h-4 mr-2" />Nouvel article
        </Button>
      </div>

      {posts === null ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" /></div>
      ) : posts.length === 0 ? (
        <p className="text-center text-slate-500 py-12">Aucun article</p>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4" data-testid={`blog-post-row-${post.id}`}>
              {post.image_url && (
                <img src={post.image_url} alt="" className="w-20 h-14 object-cover rounded-lg flex-shrink-0" />
              )}
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium text-slate-900">{post.title_fr}</p>
                <p className="text-sm text-slate-500">{post.title_nl}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{catLabels[post.category] || post.category}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${post.published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {post.published ? "Publié" : "Brouillon"}
                  </span>
                  <span className="text-xs text-slate-400">/blog/{post.slug}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => togglePublished(post)} data-testid={`blog-toggle-${post.id}`}>
                  {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(post)} data-testid={`blog-edit-${post.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(post)} data-testid={`blog-delete-${post.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-5 mt-6" data-testid="newsletter-subscribers-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-orange-500" />
            <h3 className="font-heading font-semibold text-slate-900">Abonnés newsletter</h3>
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700" data-testid="newsletter-count">{subscribers.length}</span>
          </div>
          {subscribers.length > 0 && (
            <Button size="sm" variant="outline" onClick={copyEmails} data-testid="newsletter-copy-button">
              <Copy className="w-4 h-4 mr-1" />Copier les emails
            </Button>
          )}
        </div>
        {subscribers.length === 0 ? (
          <p className="text-sm text-slate-500">Aucun abonné pour le moment. Le formulaire d'inscription est visible sur le blog.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {subscribers.map((sub) => (
              <div key={sub.id} className="flex items-center justify-between py-2" data-testid={`subscriber-${sub.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-slate-700 truncate">{sub.email}</span>
                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500 uppercase">{sub.lang}</span>
                  <span className="text-xs text-slate-400 hidden sm:inline">{new Date(sub.created_at).toLocaleDateString("fr-BE")}</span>
                </div>
                <button onClick={() => deleteSubscriber(sub)} className="p-1.5 text-slate-400 hover:text-red-500 rounded" data-testid={`subscriber-delete-${sub.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="blog-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifier l'article" : "Nouvel article"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Titre (FR) *</Label>
                <Input value={form.title_fr} onChange={(e) => setField("title_fr", e.target.value)} data-testid="blog-title-fr-input" />
              </div>
              <div>
                <Label className="mb-2 block">Titel (NL) *</Label>
                <Input value={form.title_nl} onChange={(e) => setField("title_nl", e.target.value)} data-testid="blog-title-nl-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Résumé (FR)</Label>
                <Textarea rows={2} value={form.excerpt_fr} onChange={(e) => setField("excerpt_fr", e.target.value)} data-testid="blog-excerpt-fr-input" />
              </div>
              <div>
                <Label className="mb-2 block">Samenvatting (NL)</Label>
                <Textarea rows={2} value={form.excerpt_nl} onChange={(e) => setField("excerpt_nl", e.target.value)} data-testid="blog-excerpt-nl-input" />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Contenu (FR) * — "## " pour un sous-titre, "- " pour une liste, **gras**</Label>
              <Textarea rows={8} value={form.content_fr} onChange={(e) => setField("content_fr", e.target.value)} className="font-mono text-sm" data-testid="blog-content-fr-input" />
            </div>
            <div>
              <Label className="mb-2 block">Inhoud (NL) *</Label>
              <Textarea rows={8} value={form.content_nl} onChange={(e) => setField("content_nl", e.target.value)} className="font-mono text-sm" data-testid="blog-content-nl-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="sm:col-span-2">
                <Label className="mb-2 block">URL de l'image</Label>
                <Input value={form.image_url} onChange={(e) => setField("image_url", e.target.value)} placeholder="https://..." data-testid="blog-image-input" />
              </div>
              <div>
                <Label className="mb-2 block">Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                  <SelectTrigger data-testid="blog-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prix">Prix</SelectItem>
                    <SelectItem value="primes">Primes</SelectItem>
                    <SelectItem value="conseils">Conseils</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.published} onCheckedChange={(v) => setField("published", v)} data-testid="blog-published-switch" />
              <Label>Publié</Label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="blog-save-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingId ? "Enregistrer" : "Créer l'article"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
